// Native
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroup,
  NgControl,
  NgModel
} from '@angular/forms';
import { Subscription } from "rxjs";

// Imported
import { PopperController } from "ngx-popper";

// Configs
import { PopperConfig } from '../../../configs/popper.config';

// Services
import { ValidateService } from './validate.service';

@Directive({
  selector: '[validate]',
})
export class ValidateDirective implements OnInit, OnDestroy {

  @Input('validate') formControl: FormControl;

  action: boolean = false;
  formGroup: FormGroup | FormArray;
  valueChanges$: Subscription;
  subscriptions: Subscription[] = [];

  @HostListener('blur') onBlur() {
    this.validate();
  }

  constructor(private _elementRef: ElementRef,
              private _renderer: Renderer2,
              private _validateService: ValidateService,
              @Optional() private _control: NgControl,
              @Optional() private _popper: PopperController) {
    this.popperInit();
  }

  ngOnInit() {
    if (!this.formControl && !this._control) return;
    this.formControlInit();
    this.formGroupInit();
  }

  ngOnDestroy() {
    if (this.valueChanges$)
      this.valueChanges$.unsubscribe();

    this.subscriptions.forEach(item => item.unsubscribe());
  }

  formControlInit() {
    if (!this.formControl) {
      if (this._control instanceof FormControlDirective)
        this.formControl = (this._control as FormControlDirective).form;

      if (this._control instanceof FormControlName || this._control instanceof NgModel)
        this.formControl = (this._control as FormControlName).control;
    }

    this.valueChanges$ = this.formControl.valueChanges.subscribe(() => {
      if (!this.action) return;

      this._popper.hide();
      this._popper.ngOnDestroy();
    });

    this.setValidateEmitter(this.formControl);
  }

  formGroupInit() {
    if (!this.formControl || !this.formControl.parent) return;

    do {
      this.formGroup = this.formControl.parent;
      this.setValidateEmitter(this.formGroup);
    } while(this.formGroup.parent);
  }

  popperInit() {
    if (!this.formControl && !this._control) return;
    if (!this._popper) return;

    const getCorrectPopperConfig = (obj: any) => {
      const newConfig = {};

      for (const key in obj)
        if (obj.hasOwnProperty(key)) {
          const prefix = 'ng-reflect-';
          const attr = prefix + key.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/\s/g, '-');

          if (!this._elementRef.nativeElement.hasAttribute(attr))
            newConfig[key] = obj[key];
        }

      return newConfig;
    };

    this._popper = Object.assign(this._popper, getCorrectPopperConfig(PopperConfig.formsError));
  }

  setValidateEmitter(form: FormGroup | FormControl | FormArray) {
    if (!form[this._validateService.propertyEmitter]) {
      form[this._validateService.propertyEmitter] = new EventEmitter<void>();
    }

    this.subscriptions.push(
      form[this._validateService.propertyEmitter].subscribe(() => {
        this.validate();
      })
    );
  }

  validate() {
    let outline = false;

    if (!this.formControl) return;

    if (this.formControl.invalid) {
      this._popper.content = '';
      const key = Object.keys(this._control['errors'])[0];
      switch (key) {
        case 'required':
          this._popper.content = this._validateService.getErrorMessage.required();
          break;
        case 'incorrectTime':
          this._popper.content = this._validateService.getErrorMessage.incorrectTime();
          break;
        case 'pattern':
          const regexp = this._control['errors'][key]['requiredPattern'];
          this._popper.content = this._validateService.getErrorMessage.pattern(regexp);
          break;
        case 'min':
          const minValue = this._control['errors'][key]['min'];
          this._popper.content = this._validateService.getErrorMessage.min(minValue);
          break;
        case 'max':
          const maxValue = this._control['errors'][key]['max'];
          this._popper.content = this._validateService.getErrorMessage.max(maxValue);
          break;
        case 'minlength':
          const min = this._control['errors'][key]['requiredLength'];
          this._popper.content = this._validateService.getErrorMessage.minlength(min);
          break;
        case 'maxlength':
          const max = this._control['errors'][key]['requiredLength'];
          this._popper.content = this._validateService.getErrorMessage.maxlength(max);
          break;
        case 'confirmPassword':
          this._popper.content = this._validateService.getErrorMessage.confirmPassword();
          break;
        case 'withoutPopper':
          outline = true;
          break;
        default:
          this._popper.content = this._validateService.getErrorMessage.unknow();
          break;
      }

      if (this._popper.content) {
        this._popper['popperContent'].text = this._popper.content;
        this._popper.show();
      }

      this.action = true;
    }

    if (outline)
      this._renderer.addClass(this._elementRef.nativeElement, 'is-invalid');
    else
      this._renderer.removeClass(this._elementRef.nativeElement, 'is-invalid');
  }
}
